// src/components/AuthenticationHandler.js

import { useEffect, useRef, useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { createUserAfterLogin, fetchUserRoles } from '../../services/authService';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthLoadingOverlay from './AuthLoadingOverlay';
import SecureStorageService from '../../services/secureStorageService';
import { useTranslation } from 'react-i18next';
import { loginRequest } from '../../msalConfig';

/**
 * AuthenticationHandler - Simplified session management
 * - Users stay logged in indefinitely (no popups or timeouts)
 * - Multi-tab support via localStorage
 * - Only logout clears authentication state
 */
const AuthenticationHandler = () => {
  const { t } = useTranslation('general');
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const initializationComplete = useRef(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Check if current route is public
  const isPublicRoute = useCallback(() => {
    const publicRoutes = ['/', '/contact', '/about-us', '/all-features', '/why-worldexplore', '/imprint', '/privacy', '/terms'];
    return publicRoutes.some(route => location.pathname === route || location.pathname.startsWith(route));
  }, [location.pathname]);

  // Enhanced redirect handling
  const handleAuthRedirect = useCallback(async (account) => {
    if (!account) return;

    setIsInitializing(true);
    
    try {
      // Acquire token silently to ensure we have a valid token
      await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });

      // Single initialization: Only create user if not already initialized
      if (!SecureStorageService.isUserInitialized() && !SecureStorageService.isUserInitializationInProgress()) {
        try {
          await createUserAfterLogin(instance, account);
        } catch (error) {
          console.error('User creation attempt failed, continuing flow:', error);
        }
      } else {
        console.log('User already initialized, skipping user creation');
      }

      // Fetch roles (always needed for UI)
      let roles = [];
      try {
        roles = await fetchUserRoles(instance, account);
        SecureStorageService.setRoles(roles);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }

      // Update session timestamp (no expiration)
      SecureStorageService.updateSessionTimestamp();

      // Handle navigation
      const previousPath = SecureStorageService.get(SecureStorageService.STORAGE_KEYS.PRE_LOGIN_PATH);
      
      if (previousPath && previousPath !== '/' && previousPath !== '/not-authorized') {
        navigate(previousPath);
      } else {
        // Default role-based navigation
        if (!Array.isArray(roles) || roles.length === 0) {
          navigate('/not-authorized');
        } else if (roles.includes('Admin')) {
          navigate('/adminDashboard');
        } else if (roles.includes('PartnerAdmin')) {
          navigate('/partnerDashboard');
        } else {
          navigate('/guides');
        }
      }
      
    } catch (error) {
      console.error('Auth handling error:', error);
      
      if (error.name === 'InteractionRequiredAuthError') {
        await instance.acquireTokenRedirect(loginRequest);
      } else {
        navigate('/not-authorized');
      }
    } finally {
      setIsInitializing(false);
    }
  }, [instance, navigate]);

  // Main authentication effect
  useEffect(() => {
    if (initializationComplete.current) return;

    // Skip authentication for public routes
    if (isPublicRoute()) {
      console.log('Public route, skipping authentication:', location.pathname);
      initializationComplete.current = true;
      return;
    }

    const handleAuthentication = async () => {
      try {
        // MSAL is already initialized in index.js via msalInstanceManager
        // Just get the active account
        const account = instance.getActiveAccount();
        
        if (account) {
          console.log('Active account found, proceeding with authentication');
          await handleAuthRedirect(account);
          initializationComplete.current = true;
        } else {
          console.log('No active account found');
          // If on landing page and no account, just mark as complete
          if (location.pathname === '/') {
            console.log('On landing page without account, skipping authentication');
          }
          initializationComplete.current = true;
        }
      } catch (error) {
        console.error('Auth handling error:', error);
        
        // If on landing page, don't navigate to not-authorized
        if (location.pathname === '/') {
          console.log('Auth error on landing page, staying on page');
          initializationComplete.current = true;
          return;
        }
        
        setIsInitializing(false);
        navigate('/not-authorized');
      }
    };

    handleAuthentication();
  }, [instance, handleAuthRedirect, navigate, location.pathname, isPublicRoute]);  // Save pre-login path
  useEffect(() => {
    if (!initializationComplete.current && location.pathname !== '/not-authorized') {
      SecureStorageService.set(SecureStorageService.STORAGE_KEYS.PRE_LOGIN_PATH, location.pathname);
    }
  }, [location]);

  return (
    <AuthLoadingOverlay 
      open={isInitializing} 
      message={t('authentication.initializing')}
    />
  );
};

export default AuthenticationHandler;